import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { listTatlStories, normalizeImageUrl } from "../lib/tatlApi";
import { API_URL, formatDate } from "../lib/utils";

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const TatlSidebarCard = ({ title, children }) => (
  <Card className="rounded-2xl border-stone-200 shadow-sm">
    <CardContent className="p-5">
      <h3 className="font-semibold text-stone-900 mb-3">{title}</h3>
      {children}
    </CardContent>
  </Card>
);

const Tatl1000 = () => {
  const [stories, setStories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listTatlStories(200);
      setStories(Array.isArray(data) ? data : []);

      // fetch a few projects for sidebar
      const pr = await fetch(`${API_URL}/projects?is_active=true`);
      const prData = await pr.json().catch(() => []);
      setProjects(Array.isArray(prData) ? prData.slice(0, 6) : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return stories;
    return stories.filter((s) => {
      const hay = `${s.title} ${s.child_name} ${s.location} ${s.excerpt}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [stories, q]);

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=2200&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-amber-950/90 via-blue-950/70 to-black/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <span className="inline-flex items-center rounded-full bg-white/15 border border-white/25 backdrop-blur-sm px-4 py-2 text-xs sm:text-sm font-medium text-white tracking-wide">
            Touch A Thousand Lives • #TATL1000
          </span>

          <h1 className="mt-5 font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
            TATL1000 Updates
          </h1>

          <p className="mt-4 text-white/85 max-w-2xl text-base sm:text-lg">
            Each story is a life touched. Each update is proof that compassion works.
            Explore the journeys, the support given, and the outcomes.
          </p>

          <div className="mt-7 max-w-xl">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, location, or keyword..."
              className="w-full rounded-2xl bg-white/95 border border-white/30 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main list */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">
                Lives Touched ({filtered.length})
              </h2>
              <Button variant="outline" className="rounded-xl" onClick={load}>
                Refresh
              </Button>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 gap-5">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-56 rounded-2xl bg-stone-200 animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <Card className="rounded-2xl border-stone-200">
                <CardContent className="p-6">
                  <p className="text-stone-700">
                    No stories found yet. Check back soon—updates are ongoing.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {filtered.map((s) => (
                  <Card
                    key={s.id}
                    className="overflow-hidden rounded-2xl border-stone-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={normalizeImageUrl(s.cover_image_url)}
                        alt={s.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = normalizeImageUrl("");
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                      <span className="absolute top-4 left-4 bg-amber-600 text-white text-xs px-3 py-1 rounded-full">
                        {formatDate(s.impact_date)}
                      </span>
                    </div>

                    <CardContent className="p-6">
                      <p className="text-xs text-amber-700 font-semibold">
                        {s.child_name} • {s.location}
                      </p>
                      <h3 className="mt-2 font-heading text-xl font-bold text-stone-900 line-clamp-2">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-stone-600 text-sm line-clamp-3">
                        {s.excerpt}
                      </p>

                      <div className="mt-5 flex items-center justify-between">
                        <Button asChild className="bg-amber-700 hover:bg-amber-800 rounded-xl">
                          <Link to={`/tatl1000/${s.slug || s.id}`}>Read More</Link>
                        </Button>

                        <Link
                          className="text-sm text-stone-500 hover:text-stone-800"
                          to={`/tatl1000/${s.slug || s.id}`}
                        >
                          View Story →
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <TatlSidebarCard title="Latest Updates">
              <div className="space-y-3">
                {(stories || []).slice(0, 8).map((s) => (
                  <Link
                    key={s.id}
                    to={`/tatl1000/${s.slug || s.id}`}
                    className="block p-3 rounded-xl border border-stone-200 hover:border-amber-300 hover:bg-amber-50 transition"
                  >
                    <p className="text-sm font-semibold text-stone-900 line-clamp-1">
                      {s.child_name}: {s.title}
                    </p>
                    <p className="text-xs text-stone-500">
                      {formatDate(s.impact_date)} • {s.location}
                    </p>
                  </Link>
                ))}
              </div>
            </TatlSidebarCard>

            <TatlSidebarCard title="Other Projects">
              <div className="space-y-3">
                {(projects || []).map((p) => (
                  <Link
                    key={p.id}
                    to={`/projects/${p.slug || p.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:border-blue-300 hover:bg-blue-50 transition"
                  >
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-stone-100 shrink-0">
                      <img
                        src={
                          p.image_url
                            ? (p.image_url.startsWith("http")
                                ? p.image_url
                                : p.image_url.startsWith("/")
                                  ? `${BACKEND_ORIGIN}${p.image_url}`
                                  : `${BACKEND_ORIGIN}/${p.image_url}`)
                            : normalizeImageUrl("")
                        }
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-stone-900 line-clamp-1">
                        {p.title}
                      </p>
                      <p className="text-xs text-stone-500 line-clamp-1">
                        {p.focus_area}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </TatlSidebarCard>

            <Card className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900">Support TATL1000</h3>
                <p className="text-stone-600 text-sm mt-2">
                  Your partnership helps us reach more lives with food, education, protection, and dignity.
                </p>
                <div className="mt-5 flex gap-3">
                  <Button asChild className="bg-blue-900 hover:bg-blue-950 rounded-xl">
                    <Link to="/donate-now">Donate</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to="/get-involved#partner">Partner</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tatl1000;
